/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Text button + MUI Menu for labeled option lists (Figma UXD-613/614).
 *
 * @file LabeledOptionsMenu.jsx
 * @author Gladson Samuel S
 * Each option must be `{ label, value }`. The `value` prop is the selected option’s `value`.
 *
 * @example
 * <LabeledOptionsMenu
 *   options={[{ value: 'std', label: 'Standard' }]}
 *   value={selectedValue}
 *   title={selectedOption ? `Due dates: ${selectedOption.label}` : null}
 *   onSelect={setSelectedOption}
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Root,
  TriggerButton,
  StartIcon,
  EndIcon,
  MenuItemStyled,
  MenuItemSelected,
  MenuItemLabel,
  CheckIcon,
  StyledMenu
} from './LabeledOptionsMenuStyles';

const MENU_ID = 'labeled-options-menu';

const LabeledOptionsMenu = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const {
    options = [],
    value,
    onSelect,
    buttonLabel,
    startIconName,
    endIconName,
    selectedCheckIconName,
    ButtonProps = {},
    MenuProps = {}
  } = props;

  const renderButtonLabel = () => {
    if (buttonLabel === undefined || buttonLabel === null) {
      return null;
    }

    return buttonLabel;
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option) => {
    if (option?.value === value) {
      return;
    }

    onSelect(option);
    handleClose();
  };

  const {
    onClick: buttonOnClick,
    startIcon: buttonStartIcon,
    endIcon: buttonEndIcon,
    ...restButtonProps
  } = ButtonProps;

  const {
    onClose: menuOnClose,
    MenuListProps: menuListPropsUser,
    ...restMenuProps
  } = MenuProps;

  const renderButton = () => (
    <TriggerButton
      {...restButtonProps}
      variant="text"
      disableRipple
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      aria-controls={open ? MENU_ID : undefined}
      id={`${MENU_ID}-trigger`}
      startIcon={buttonStartIcon || (
        <StartIcon name={startIconName} />
      )}
      endIcon={buttonEndIcon || (
        <EndIcon name={endIconName} />
      )}
      onClick={(e) => {
        if (buttonOnClick) {
          buttonOnClick(e);
        }

        handleOpen(e);
      }}
    >
      {renderButtonLabel()}
    </TriggerButton>
  );

  const renderMenuList = (option, index) => {
    if (!option) {
      return null;
    }

    const selected = option.value === value;
    const itemKey = option.value != null ? String(option.value) : `opt-${index}`;
    const ItemComponent = selected ? MenuItemSelected : MenuItemStyled;

    return (
      <ItemComponent
        key={itemKey}
        disableRipple
        selected={selected}
        onClick={() => handleSelect(option)}
      >
        <MenuItemLabel>{option.label}</MenuItemLabel>
        {selected ? (
          <CheckIcon name={selectedCheckIconName} />
        ) : (
          <CheckIcon name={selectedCheckIconName} aria-hidden style={{ visibility: 'hidden' }} />
        )}
      </ItemComponent>
    );
  };

  const renderMenu = () => {
    if (!options || options.length === 0) {
      return null;
    }

    return (
      <StyledMenu
        {...restMenuProps}
        id={MENU_ID}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={(e, reason) => {
          if (menuOnClose) {
            menuOnClose(e, reason);
          }

          handleClose();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        MenuListProps={{
          disablePadding: true,
          ...menuListPropsUser
        }}
      >
        {options.map((option, index) => renderMenuList(option, index))}
      </StyledMenu>
    );
  };

  return (
    <Root>
      {renderButton()}
      {renderMenu()}
    </Root>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
  label: PropTypes.node.isRequired
});

LabeledOptionsMenu.propTypes = {
  options: PropTypes.arrayOf(optionShape),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  onSelect: PropTypes.func.isRequired,
  buttonLabel: PropTypes.node,
  startIconName: PropTypes.string,
  endIconName: PropTypes.string,
  selectedCheckIconName: PropTypes.string,
  classes: PropTypes.object,
  ButtonProps: PropTypes.object,
  MenuProps: PropTypes.object
};

LabeledOptionsMenu.defaultProps = {
  options: [],
  value: undefined,
  buttonLabel: undefined,
  startIconName: 'CalendarIcon',
  endIconName: 'CaretUpIcon',
  selectedCheckIconName: 'TickIcon',
  classes: undefined,
  ButtonProps: {},
  MenuProps: {}
};

export default LabeledOptionsMenu;
